// src/components/pages/settings/ContractSettings.tsx
import { Alert, Badge, Box, Flex, Stack, Text, Button, Spinner } from '@chakra-ui/react'
import { LuSquareX, LuRotateCcw } from 'react-icons/lu'
import { useEffect, useRef, useState } from 'react'
import { CancelSubscriptionModal } from '../../../components/modals/CancelSubscriptionModal'
import { ReactivateSubscriptionModal } from '../../../components/modals/ReactivateSubscriptionModal'
import { getContractSettings, cancelContract, reactivateContract } from '@/api/settings.api'
import type { SubscriptionStatus } from '@/api/dto/settings.dto'
import { FormLayout } from '@/components/layout/FormLayout'

type SubscriptionResponse = {
  bank: string | null
  iban: string | null
  bic: string | null
  startDate: string | null
  endDate: string | null
  subscriptionStatus: SubscriptionStatus
  cancelledAt: string | null
  reactivatedAt: string | null
  priceGrossPerYear: number | null
}

type UiStatus = SubscriptionStatus | 'NONE'

const STATUS_CONFIG: Record<UiStatus, { label: string; colorPalette: string }> = {
  ACTIVE: { label: 'Aktiv', colorPalette: 'green' },
  CANCELLED: { label: 'Gekündigt', colorPalette: 'orange' },
  EXPIRED: { label: 'Ausgelaufen', colorPalette: 'purple' },
  NONE: { label: 'Kein Abonnement', colorPalette: 'gray' },
}

type FormState = {
  bank: string
  iban: string
  bic: string
}

type StatusState =
  | { type: 'idle' }
  | { type: 'success'; message: string }
  | { type: 'error'; message: string }

export function ContractSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null)
  const [form, setForm] = useState<FormState>({
    bank: '',
    iban: '',
    bic: '',
  })
  const [status, setStatus] = useState<StatusState>({ type: 'idle' })
  const [cancelPopupVisible, setCancelPopupVisible] = useState(false)
  const [reactivatePopupVisible, setReactivatePopupVisible] = useState(false)
  const uiStatus: UiStatus = subscription?.subscriptionStatus ?? 'NONE'

  // remember original values so we can send only changes
  const initialFormRef = useRef<FormState | null>(null)

  // const isDirty = useMemo(() => {
  //   if (!initialFormRef.current) return false
  //   return (
  //     form.bank !== initialFormRef.current.bank ||
  //     form.iban !== initialFormRef.current.iban ||
  //     form.bic !== initialFormRef.current.bic
  //   )
  // }, [form])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setStatus({ type: 'idle' })

        // 1) Call the shared API helper
        const dto = await getContractSettings()

        // 2) Map backend DTO -> UI shape
        const subscription: SubscriptionResponse = {
          bank: dto.bank,
          iban: dto.iban,
          bic: dto.bic,
          startDate: dto.startDate,
          endDate: dto.endDate,
          subscriptionStatus: dto.subscriptionStatus,
          cancelledAt: dto.cancelledAt,
          reactivatedAt: dto.reactivatedAt,
          priceGrossPerYear: dto.price,
        }

        setSubscription(subscription)

        // 3) Hydrate the form (null -> '')
        const nextForm: FormState = {
          bank: subscription.bank ?? '',
          iban: subscription.iban ?? '',
          bic: subscription.bic ?? '',
        }

        setForm(nextForm)
        initialFormRef.current = nextForm
      } catch (error: any) {
        console.error(error)

        // Optional: if you want to react specifically to unauthorized
        if (error.code === 'UNAUTHORIZED') {
          // e.g. navigate('/login') or rely on AuthContext handling
        }

        setStatus({
          type: 'error',
          message: 'Fehler beim Laden der Vertragsdaten. Bitte versuchen Sie es später erneut.',
        })
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  // const handleChange =
  //   (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
  //     setForm(prev => ({ ...prev, [field]: event.target.value }))
  //     // clear previous status when user edits something
  //     if (status.type !== 'idle') {
  //       setStatus({ type: 'idle' })
  //     }
  //   }

  // const handleReset = () => {
  //   if (!initialFormRef.current) return
  //   setForm(initialFormRef.current)
  //   setStatus({ type: 'idle' })
  // }

  const openCancelContractDialog = () => {
    setCancelPopupVisible(true)
  }

  // const handleSubmit = async () => {
  //   if (!initialFormRef.current || !isDirty) return

  //   // --- validations (unchanged, just grouped) ---

  //   // Bank length ≤ 100
  //   if (form.bank && form.bank.length > 100) {
  //     setStatus({
  //       type: 'error',
  //       message: 'Der Bankname muss weniger als 100 Zeichen lang sein.',
  //     })
  //     return
  //   }

  //   // IBAN exactly 22 characters (if present)
  //   if (form.iban && (form.iban.length < 15 || form.bic.length > 34)) {
  //     setStatus({
  //       type: 'error',
  //       message: 'Die IBAN muss 15–34 Zeichen lang sein.',
  //     })
  //     return
  //   }

  //   // BIC length 8–11 (if present)
  //   if (form.bic && (form.bic.length < 8 || form.bic.length > 11)) {
  //     setStatus({
  //       type: 'error',
  //       message: 'Die BIC muss 8–11 Zeichen lang sein.',
  //     })
  //     return
  //   }

  //   // If nothing changed, do nothing
  //   if (!isDirty) return

  //   // --- build payload exactly as Swagger expects: all strings, no null ---
  //   const payload = {
  //     bank: form.bank.trim(), // "" if empty
  //     iban: form.iban.trim(), // "" if empty (but you probably want it required)
  //     bic: form.bic.trim(), // "" if empty
  //   }

  //   try {
  //     setSaving(true)
  //     setStatus({ type: 'idle' })

  //     const updatedDto = await updateContractSettings(payload)

  //     const updated: SubscriptionResponse = {
  //       bank: updatedDto.bank,
  //       iban: updatedDto.iban,
  //       bic: updatedDto.bic,
  //       startDate: updatedDto.startDate,
  //       endDate: updatedDto.endDate,
  //       isTerminated: updatedDto.isTerminated,
  //       terminatedOn: updatedDto.terminatedOn,
  //       priceGrossPerYear: subscription?.priceGrossPerYear ?? null,
  //     }

  //     setSubscription(updated)

  //     const nextForm: FormState = {
  //       bank: updated.bank ?? '',
  //       iban: updated.iban ?? '',
  //       bic: updated.bic ?? '',
  //     }

  //     setForm(nextForm)
  //     initialFormRef.current = nextForm

  //     setStatus({
  //       type: 'success',
  //       message: 'Änderungen gespeichert. Ihre Zahlungsdaten wurden aktualisiert.',
  //     })
  //   } catch (error: any) {
  //     console.error(error)
  //     setStatus({
  //       type: 'error',
  //       message: error?.message || 'Fehler beim Speichern. Bitte versuchen Sie es später erneut.',
  //     })
  //   } finally {
  //     setSaving(false)
  //   }
  // }

  // const formatDate = (iso: string | null) => {
  //   if (!iso) return '–'
  //   try {
  //     return new Intl.DateTimeFormat('de-DE').format(new Date(iso))
  //   } catch {
  //     return '–'
  //   }
  // }

  const handleConfirmCancel = async () => {
    if (!subscription) return

    try {
      setSaving(true)
      setStatus({ type: 'idle' })

      const updatedDto = await cancelContract()

      const updated: SubscriptionResponse = {
        bank: updatedDto.bank,
        iban: updatedDto.iban,
        bic: updatedDto.bic,
        startDate: updatedDto.startDate,
        endDate: updatedDto.endDate,
        subscriptionStatus: updatedDto.subscriptionStatus,
        cancelledAt: updatedDto.cancelledAt,
        reactivatedAt: updatedDto.reactivatedAt,
        priceGrossPerYear: subscription.priceGrossPerYear,
      }

      setSubscription(updated)

      setStatus({
        type: 'success',
        message: 'Ihr Vertrag wurde gekündigt.',
      })
    } catch (error: any) {
      console.error(error)
      setStatus({
        type: 'error',
        message:
          error?.message ||
          'Fehler beim Kündigen des Vertrags. Bitte versuchen Sie es später erneut.',
      })
    } finally {
      setSaving(false)
    }
  }

  const openReactivateContractDialog = () => {
    setReactivatePopupVisible(true)
  }

  const handleConfirmReactivate = async () => {
    if (!subscription) return

    try {
      setSaving(true)
      setStatus({ type: 'idle' })

      const updatedDto = await reactivateContract()

      const updated: SubscriptionResponse = {
        bank: updatedDto.bank,
        iban: updatedDto.iban,
        bic: updatedDto.bic,
        startDate: updatedDto.startDate,
        endDate: updatedDto.endDate,
        subscriptionStatus: updatedDto.subscriptionStatus,
        cancelledAt: updatedDto.cancelledAt,
        reactivatedAt: updatedDto.reactivatedAt,
        priceGrossPerYear: updatedDto.price,
      }

      setSubscription(updated)

      setStatus({
        type: 'success',
        message: 'Ihr Vertrag wurde reaktiviert.',
      })
    } catch (error: any) {
      console.error(error)
      setStatus({
        type: 'error',
        message:
          error?.message ||
          'Fehler beim Reaktivieren des Vertrags. Bitte versuchen Sie es später erneut.',
      })
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return ''

    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr // fallback if backend sends weird format

    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()

    return `${day}.${month}.${year}`
  }

  const formatContractPeriod = () => {
    if (!subscription?.startDate || !subscription?.endDate) return '–'
    return `${formatDate(subscription.startDate)}–${formatDate(subscription.endDate)}`
  }

  const formatPrice = () => {
    if (!subscription || subscription.priceGrossPerYear == null) return '–'
    return `${new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(subscription.priceGrossPerYear)} € p.a. (brutto)`
  }

  const getStatusMessage = () => {
    switch (uiStatus) {
      case 'CANCELLED':
        return `Ihr Vertrag wurde gekündigt. Sie können das SIB Portal noch bis zum ${formatDate(
          subscription?.endDate
        )} nutzen.`
      case 'EXPIRED':
        return 'Ihr Vertrag ist abgelaufen. Der Zugriff auf das SIB Portal ist gesperrt. Reaktivieren Sie Ihren Vertrag, um wieder Zugriff zu erhalten.'
      case 'NONE':
        return 'Es liegt kein aktives Abonnement vor. Der Zugriff auf das SIB Portal ist gesperrt. Reaktivieren Sie Ihr Abonnement, um wieder Zugriff zu erhalten.'
      default:
        return null
    }
  }

  return (
    <Box>
      {loading ? (
        <Flex justify="center" py="16">
          <Spinner />
        </Flex>
      ) : (
        <FormLayout.Root>
          {/* Abonnement */}
          <FormLayout.Section data-testid="contract-settings">
            <FormLayout.Title>Abonnement</FormLayout.Title>

            <FormLayout.Description>
              Hier sehen Sie Ihre verbleibende Vertragslaufzeit. Zusätzlich können Sie hier Ihren
              Vertrag kündigen.
            </FormLayout.Description>

            <Stack gap={0} width="fit-content">
              {/* Row: Vertragslaufzeit */}
              <Box textStyle="smNormal" borderBottomWidth="1px" borderColor="border" py="3">
                <Box
                  display="grid"
                  gridTemplateColumns="220px auto"
                  columnGap="32px"
                  width="fit-content"
                >
                  <Text color="fg.muted">Vertragslaufzeit</Text>
                  <Text>{formatContractPeriod()}</Text>
                </Box>
              </Box>

              {/* Row: Vertragsstatus */}
              <Box textStyle="smNormal" borderBottomWidth="1px" borderColor="border" py="3">
                <Box
                  display="grid"
                  gridTemplateColumns="220px auto"
                  columnGap="32px"
                  width="fit-content"
                  alignItems="center"
                >
                  <Text color="fg.muted">Vertragsstatus</Text>
                  <Badge
                    colorPalette={STATUS_CONFIG[uiStatus].colorPalette}
                    variant="solid"
                    size="sm"
                  >
                    {STATUS_CONFIG[uiStatus].label}
                  </Badge>
                </Box>
              </Box>

              {/* Row: Vertrag gekündigt am */}
              <Box textStyle="smNormal" borderBottomWidth="1px" borderColor="border" py="3">
                <Box
                  display="grid"
                  gridTemplateColumns="220px auto"
                  columnGap="32px"
                  width="fit-content"
                  alignItems="center"
                >
                  <Text color="fg.muted">Vertrag gekündigt am</Text>
                  {subscription?.cancelledAt ? (
                    <Badge colorPalette="red" variant="subtle" size="sm">
                      {formatDate(subscription.cancelledAt)}
                    </Badge>
                  ) : (
                    <Text>–</Text>
                  )}
                </Box>
              </Box>

              {/* Row: Kosten */}
              <Box textStyle="smNormal" borderBottomWidth="1px" borderColor="border" py="3">
                <Box
                  display="grid"
                  gridTemplateColumns="220px auto"
                  columnGap="32px"
                  width="fit-content"
                >
                  <Text color="fg.muted">Kosten</Text>
                  <Text>{formatPrice()}</Text>
                </Box>
              </Box>
            </Stack>
          </FormLayout.Section>

          {/* Status-specific message */}
          {getStatusMessage() && (
            <FormLayout.Description>{getStatusMessage()}</FormLayout.Description>
          )}

          {/* Status messages */}
          {status.type !== 'idle' && (
            <Alert.Root status={status.type}>
              <Alert.Indicator />
              <Alert.Description>{status.message}</Alert.Description>
            </Alert.Root>
          )}

          {/* Action buttons */}
          <FormLayout.Section>
            <FormLayout.Title>SIB Portal kündigen</FormLayout.Title>

            <FormLayout.Actions>
              <Button
                variant="subtle"
                colorPalette="red"
                onClick={openCancelContractDialog}
                disabled={uiStatus !== 'ACTIVE'}
              >
                <LuSquareX />
                Vertrag kündigen
              </Button>

              <Button
                variant="subtle"
                colorPalette="blue"
                onClick={openReactivateContractDialog}
                disabled={saving || uiStatus === 'ACTIVE'}
              >
                <LuRotateCcw />
                Vertrag reaktivieren
              </Button>
            </FormLayout.Actions>
          </FormLayout.Section>
        </FormLayout.Root>
      )}
      <CancelSubscriptionModal
        isOpen={cancelPopupVisible}
        onClose={() => setCancelPopupVisible(false)}
        onCancel={() => {
          console.log('cancel dialog')
          // Optional: extra side-effect when the user clicks "Abbrechen" in the dialog
          // For now, onClose already closes the modal.
        }}
        onConfirm={handleConfirmCancel}
      />
      <ReactivateSubscriptionModal
        isOpen={reactivatePopupVisible}
        onClose={() => setReactivatePopupVisible(false)}
        onConfirm={handleConfirmReactivate}
      />
    </Box>
  )
}
